import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ExternalLink, FileText } from "lucide-react";
import React from "react";
import { highlightText } from "../lib/utils";
import { Legislation } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

interface LegislationListProps {
  legislation: Legislation[];
  loading: boolean;
  searchTerm?: string;
}

const LegislationList: React.FC<LegislationListProps> = ({
  legislation,
  loading,
  searchTerm = "",
}) => {
  // Formatear fecha
  // const formatDate = (dateString: string) => {
  //   try {
  //     return format(new Date(dateString), "dd MMM yyyy", { locale: es });
  //   } catch (e) {
  //     return dateString;
  //   }
  // };

  // Renderizar esqueletos durante la carga
  if (loading) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <Skeleton className="h-8 w-full max-w-md mb-4" />
        </div>
        <div className="space-y-4">
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="flex flex-col space-y-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
        </div>
      </div>
    );
  }

  // Renderizar mensaje cuando no hay resultados
  if (!loading && legislation.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <FileText className="h-12 w-12 text-gray-400 mb-4" />
        <p className="text-lg font-medium mb-2">No se encontraron resultados</p>
        <p className="text-gray-500">
          Prueba a cambiar los filtros de búsqueda
        </p>
      </div>
    );
  }

  const parseDate = (dateString: string) => {
    // Convertir la fecha en formato YYYYMMDD a un objeto Date
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10) - 1; // Los meses son 0-indexados
    const day = parseInt(dateString.substring(6, 8), 10);
    return new Date(year, month, day);
  };

  // Renderizar la tabla de legislación
  return (
    <div className="p-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Título</TableHead>
            <TableHead>Fecha de Actualización</TableHead>
            <TableHead>Fecha de Publicación</TableHead>
            <TableHead>Departamento</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {legislation.map((item) => (
            <TableRow key={item.identificador}>
              <TableCell>
                <div className="font-medium">
                  {searchTerm
                    ? highlightText(item.titulo, searchTerm)
                    : item.titulo}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {item.referencia}
                </div>
              </TableCell>
              <TableCell>
                {format(parseDate(item.fecha_actualizacion), "dd/MM/yyyy", {
                  locale: es,
                })}
              </TableCell>
              <TableCell>
                {format(parseDate(item.fecha_publicacion), "dd/MM/yyyy", {
                  locale: es,
                })}
              </TableCell>
              <TableCell>{item.departamento.texto}</TableCell>
              <TableCell>
                <Badge
                  variant={
                    item.estado_consolidacion.codigo === "4"
                      ? "default"
                      : "secondary"
                  }
                >
                  {item.estado_consolidacion.texto}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={item.url_html_consolidada}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Ver
                  </a>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default LegislationList;
